import { signUp, confirmSignUp, signIn, signOut, getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';

export interface SignUpParameters {
  email: string;
  password: string;
  name?: string;
}

export interface SignInParameters {
  email: string;
  password: string;
}

export interface ConfirmSignUpParameters {
  username: string;
  confirmationCode: string;
}

export class AuthService {
  static async signUp({ email, password, name }: SignUpParameters) {
    try {
      // Generate a unique username that's not in email format
      const username = `user_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: username,
        password,
        options: {
          userAttributes: {
            email,
            ...(name && { name }),
          },
        },
      });

      return {
        success: true,
        isSignUpComplete,
        userId,
        nextStep,
        username, // Return the generated username for confirmation
      };
    } catch (error) {
      console.error('Error signing up:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  static async confirmSignUp({ username, confirmationCode }: ConfirmSignUpParameters) {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username,
        confirmationCode,
      });

      return {
        success: true,
        isSignUpComplete,
        nextStep,
      };
    } catch (error) {
      console.error('Error confirming sign up:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  static async signIn({ email, password }: SignInParameters) {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
      });

      return {
        success: true,
        isSignedIn,
        nextStep,
      };
    } catch (error) {
      console.error('Error signing in:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  static async signOut() {
    try {
      await signOut();
      return {
        success: true,
      };
    } catch (error) {
      console.error('Error signing out:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  static async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      return {
        success: true,
        user,
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'User not authenticated',
        user: null,
      };
    }
  }

  static async getAuthSession() {
    try {
      const session = await fetchAuthSession();
      return {
        success: true,
        session,
        isAuthenticated: !!session.tokens?.accessToken,
      };
    } catch (error) {
      console.error('Error getting auth session:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'No active session',
        isAuthenticated: false,
      };
    }
  }
}